import { useEffect, useState } from 'react';

import type { SidekickRecipient } from '../../contexts/SidekickApiContext';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useSidekickApi } from '../../hooks/useSidekickApi';
import type { ShareRecipient } from './types';

/** Suggestions for the current query, plus whether a lookup is in flight. */
export type UseRecipientSearchResult = {
  options: ShareRecipient[];
  isLoading: boolean;
};

/**
 * The matches in hand, together with the query that produced them.
 *
 * Pairing the two is what lets the field tell a current suggestion
 * from a leftover one: anything whose query is not the text now in the
 * box is stale, whether the lookup for that text has been sent yet or
 * is still in flight.
 */
type RecipientMatches = {
  query: string;
  options: ShareRecipient[];
};

/**
 * Every suggestion reaches the field through this hook, which is why
 * the directory tag is stamped here rather than asked of the host: a
 * host cannot forget it or get it wrong.
 */
const toDirectoryRecipient = (
  recipient: SidekickRecipient
): ShareRecipient => ({
  ...recipient,
  source: 'directory',
});

/**
 * Resolves directory suggestions for what the user is typing into the
 * recipients field.
 *
 * The host supplies the lookup through `SidekickApiContext`. Without
 * one there is nothing to search, so the hook stays empty and idle and
 * the field falls back to free-typed addresses.
 *
 * Each query aborts the one before it, so a slow earlier response can
 * never land on top of a newer one. Only matches for the text now in
 * the field are offered: a keystroke retires the ones in hand at once
 * rather than after the debounce.
 *
 * @param query - Raw text the user has typed, undebounced.
 * @returns The suggestion list and its loading state.
 */
export const useRecipientSearch = (query: string): UseRecipientSearchResult => {
  const { searchRecipients } = useSidekickApi();
  const debouncedQuery = useDebouncedValue(query);
  const [matches, setMatches] = useState<RecipientMatches>({
    query: '',
    options: [],
  });

  useEffect(() => {
    if (searchRecipients === undefined || debouncedQuery.trim() === '') {
      setMatches({ query: debouncedQuery, options: [] });
      return;
    }

    const controller = new AbortController();

    const settle = (results: SidekickRecipient[]) => {
      if (controller.signal.aborted) {
        return;
      }
      setMatches({
        query: debouncedQuery,
        options: results.map(toDirectoryRecipient),
      });
    };

    // A failed lookup resolves to the same empty list a successful one
    // with no matches would: the field has no error state of its own,
    // and the user can still type the address by hand.
    searchRecipients(debouncedQuery, controller.signal)
      .then(settle)
      .catch(() => settle([]));

    return () => {
      controller.abort();
    };
  }, [debouncedQuery, searchRecipients]);

  // Whether the text now in the box is worth a lookup at all. A host
  // without a directory has nothing to search, and a blank field is
  // not a query.
  const isSearchable = searchRecipients !== undefined && query.trim() !== '';

  // Both values track the raw query, not the debounced one. The field
  // re-filters nothing of its own, so rows left from the previous query
  // would otherwise stay selectable for the whole debounce window —
  // long enough to pick a match for text no longer in the box. Standing
  // on the loading row through that gap also keeps the popup from
  // blinking shut between queries.
  const isStale = matches.query !== query;

  return {
    options: isStale ? [] : matches.options,
    isLoading: isStale && isSearchable,
  };
};

export default useRecipientSearch;
